import { Flex } from '@chakra-ui/layout';
import { Menu, MenuButton } from '@chakra-ui/menu';
import { Box, Icon, IconButton, Portal } from '@chakra-ui/react';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';

import { DeliveryStatus } from '@revelio/data-access';
import { Tag, type TagColorScheme } from '@revelio/ui';

import gridStyles from '../../../shared/ui/table/status-grid-theme.module.css';
import {
  type StatusTone,
  ThemedStatusTag,
} from '../../../shared/ui/table/themed-status-tag';
import { useIsThemedAppearance } from '../../../shared/user-preferences/hooks/useIsThemedAppearance';
import type { ReportRun } from '../../CustomDash/components/Reports/types';
import { CustomReportMenuList } from '../components/custom-report-menu-list';
import { MiddlewareReportMenuList } from '../components/mw-report-menu-list';
import type { useAllReports } from '../hooks/use-all-reports';
import styles from './reports-status-column.module.css';

// Legacy appearance: @revelio/ui Tag colour scheme (the migrated solid tag).
const tagStatusColorScheme: Record<string, TagColorScheme> = {
  [DeliveryStatus.Running]: 'blue',
  [DeliveryStatus.Failed]: 'red',
  [DeliveryStatus.Finished]: 'green',
  [DeliveryStatus.FinishedWithEmpty]: 'green',
};

// Themed appearance: squared status pill tone per status (Figma `138:16578`).
// Scheduled is normalised to Running before lookup (see `tagStatus`), mirroring
// `tagStatusColorScheme`.
const tagStatusTone: Record<string, StatusTone> = {
  [DeliveryStatus.Running]: 'inProgress',
  [DeliveryStatus.Failed]: 'failed',
  [DeliveryStatus.Finished]: 'finished',
  [DeliveryStatus.FinishedWithEmpty]: 'finished',
};

export const ReportsStatusColumn = ({
  viewReportLink,
  value,
  report,
  customReportRun,
  refreshReports,
}: {
  viewReportLink: string | null;
  report: ReturnType<typeof useAllReports>['reports'][number] | null;
  customReportRun: ReportRun | null;
  reportStatus: DeliveryStatus;
  value: string;
  refreshReports: () => void;
}) => {
  const showMenu =
    [DeliveryStatus.Finished, DeliveryStatus.Failed].includes(
      value as DeliveryStatus
    ) && report;

  const stopPropagation = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  const tagStatus =
    value === DeliveryStatus.Scheduled ? DeliveryStatus.Running : value;

  const isThemedAppearance = useIsThemedAppearance();

  return (
    <Flex justifyContent="space-between">
      {isThemedAppearance ? (
        <ThemedStatusTag tone={tagStatusTone[tagStatus] ?? 'neutral'}>
          {tagStatus}
        </ThemedStatusTag>
      ) : (
        <Tag
          size="md"
          variant="solid"
          colorScheme={tagStatusColorScheme[tagStatus] ?? 'gray'}
          className={styles.statusTag}
        >
          {tagStatus}
        </Tag>
      )}

      {showMenu && (
        <Menu placement="left-start">
          <MenuButton
            as={IconButton}
            className={gridStyles.rowActionButton}
            borderRadius="2px"
            icon={
              <Icon
                as={IconDotGrid1x3Vertical}
                data-testid="reports_status_menu_button"
              />
            }
            size="xs"
            colorScheme="gray"
            onClick={stopPropagation}
          >
            Actions
          </MenuButton>
          <Portal>
            <Box onClick={stopPropagation}>
              {report?.reportType === 'CustomReport' ? (
                <CustomReportMenuList
                  reportRun={customReportRun as ReportRun}
                  onActionSucceeded={() => {
                    refreshReports();
                  }}
                />
              ) : (
                <MiddlewareReportMenuList
                  viewReportLink={viewReportLink}
                  report={report}
                  onActionSucceeded={refreshReports}
                />
              )}
            </Box>
          </Portal>
        </Menu>
      )}
    </Flex>
  );
};
